/* eslint-disable react/prop-types */
import {useContext, useRef} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {traceInitialProps} from '@s-ui/logger'
import AtomButton, {atomButtonDesigns} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMediaQuery, useOnScreen} from '@s-ui/react-hooks'
import AdDisplay from '@adv-mt/mt-ad-display'
import ButtonSticky from '@adv-mt/mt-button-sticky'
import Layout from '@adv-mt/mt-layout-basic'
import {useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import ListModelDetails, {listTypes} from '@adv-mt/mt-list-model-details'
import ListModels from '@adv-mt/mt-list-models'
import ListRelatedAds, {listRelatedAdsDisplay} from '@adv-mt/mt-list-related-ads'
import MotorTagCloud from '@adv-mt/mt-list-tagcloud'
import MediaDimensions from '@adv-mt/mt-media-dimensions'
import Breadcrumb from '@adv-mt/mt-navigation-breadcrumb'
import PanelBestOffers from '@adv-mt/mt-panel-best-offers'
import PanelEquipment, {panelEquipmentDisplayTypes} from '@adv-mt/mt-panel-equipment'
import SectionWrapper from '@adv-mt/mt-shared-section-wrapper'
import TechnicalDetailsPanelLinks from '@adv-mt/mt-technical-details-panel-links'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import MotorTrackingCriteo from '@adv-mt/mt-tracking-criteo'
import TrackingPageView from '@adv-mt/mt-tracking-page-view'

import LinkFactory from '../../../components/LinkFactory/index.js'
import {withThirdPartiesParam} from '../../../hocs/withThirdPartiesParam.js'
import GalleryWrapper from '../../ModelDetail/components/GalleryWrapper.js'
import {VehicleSize} from './components/vehicle-size.js'
import {getInitialProps} from './getInitialProps.js'
import {
  BASE_CLASS,
  BEST_OFFERS_SECTION_ELEMENT_ID,
  LOW_DASH,
  MAX_NUM_OF_ADS,
  MODEL_DETAILS_INFO_CLASS,
  PANEL_EQUIPMENT_INFO_CLASS,
  SCROLL_PARAMS,
  SPACE_STRING
} from './settings.js'
const transformToUrl = string => string.toLocaleLowerCase().replaceAll(SPACE_STRING, LOW_DASH)

const TechnicalDetailsVehicle = ({
  adsKm0 = [],
  adsNew = [],
  adsRenting = [],
  basicDataSheet,
  bestOffers,
  bodyType,
  breadcrumbs,
  dimensionsImage,
  doors,
  fuelType,
  hasJato = false,
  hasNewVehicles,
  helmet = {},
  loadThirdParties,
  makeId,
  makeName,
  makeUrlParam,
  modelId,
  modelName,
  modelUrlParam,
  pageInfoResponse = {},
  pageName,
  serverErrors = [],
  tagclouds = [],
  photos = [],
  versionId,
  versionName = '',
  year
}) => {
  const {domain, i18n} = useContext(Context)
  const bestOffersSectionRef = useRef()
  const [isBestOffersOnScreen] = useOnScreen({
    ref: bestOffersSectionRef,
    once: false
  })

  const hasBestOffers = Boolean(bestOffers?.length)

  const config = domain.get('config')
  const appName = config.get('APP_NAME')
  const {ADS_LIST_SUBSCRIPTION_PATH_PATTERN, KM0_PATH_PATTERN, NEW_KM0_PATH_PATTERN} = config.get('page')
  const showMediaDimensions = config.get('features').HAS_MAIN_IMAGE_ENABLED
  const isMobile = config.get('isMobile')
  const isHigherThanMobileResolution = useMediaQuery(useMediaQueryBreakpoints.sm)

  const hasKm0Ads = Boolean(adsKm0.length)
  const hasNewAds = Boolean(adsNew.length)
  const hasRentingAds = Boolean(adsRenting.length)

  const make = {id: makeId, name: makeName}
  const model = {id: modelId, name: modelName}
  const makeModel = `${makeName} ${modelName}`
  const linkToKm0Ads = transformToUrl(`/${makeName}/${modelName}${KM0_PATH_PATTERN}`)
  const linkToNewAds = transformToUrl(`/${makeName}/${modelName}${NEW_KM0_PATH_PATTERN}`)
  const linkToRenting = transformToUrl(`${ADS_LIST_SUBSCRIPTION_PATH_PATTERN}${makeName}/${modelName}`)

  const {
    equipment,
    groupedSpecifications: {body = {}, engine: {hp: groupedHp} = {}} = {},
    relatedVehicles = [],
    specifications: {hp} = {}
  } = pageInfoResponse

  const {width, height, length} = body

  const hasSizes = Boolean(width) || Boolean(height) || Boolean(length)

  const hasCriticalServerErrors = Boolean(serverErrors.length)

  const hasRelatedVehicles = Boolean(relatedVehicles.length)

  const jatoDescription = i18n.t('TECHNICAL_DETAILS_JATO_DESCRIPTION')

  const hasHp = Boolean(hp || groupedHp)

  const competitorsTitle = i18n.t('TECHNICAL_DETAILS_MODEL_COMPETITORS_TITLE', {
    makeName,
    modelName: `${modelName} ${versionName}`
  })

  const panelSliderTitle = {
    literal: 'TECHNICAL_DETAILS_VEHICLE_TITLE',
    params: {
      makeName,
      modelName,
      versionName,
      hp: hasHp ? `${hp || groupedHp} ${i18n.t('COMMON.UNIT_HP')}` : '',
      year
    }
  }

  const linksTitle = i18n.t(`TECHNICAL_DETAILS_VEHICLE_LINKS_${appName}_TITLE`, {
    makeName,
    modelName
  })

  const modelDetailsSize = i18n.t('TECHNICAL_DETAILS_MODEL_DETAILS_SIZE', {
    makeName,
    modelName
  })

  const mainEquipmentTitle = i18n.t('COMMON.EQUIPMENT.MAIN_SPECIFICATIONS')

  const hasBasicDataSheet = Boolean(basicDataSheet)
  const hasEquipment = Boolean(equipment)

  const _handleScrollToBestOffers = () => {
    const elementBestOffersSection = document.getElementById('best-offers-id')
    elementBestOffersSection && elementBestOffersSection.scrollIntoView(SCROLL_PARAMS)
  }

  const isMobileBestOffersSection = isMobile && hasBestOffers
  const isDesktopBestOffersSection = !isMobile && hasBestOffers

  const contentWrapperBaseClassName = `${BASE_CLASS}-contentWrapper`
  const contentWrapperClassName = cx(contentWrapperBaseClassName, {
    [`${contentWrapperBaseClassName}--withSidebar`]: hasBestOffers
  })

  return !hasCriticalServerErrors && Boolean(pageInfoResponse) ? (
    <Layout.Basic linkFactory={LinkFactory}>
      <main className={contentWrapperClassName}>
        <section className={`${BASE_CLASS}-content`}>
          <TitleBasic
            as={titleBasicTags.h1}
            title={i18n.t(panelSliderTitle.literal, {...panelSliderTitle.params})}
            size={titleBasicSizes.m}
          />
          <SectionWrapper>
            <GalleryWrapper photos={photos} alt={makeModel} />
          </SectionWrapper>
          {hasSizes && (
            <SectionWrapper title={modelDetailsSize}>
              {showMediaDimensions ? (
                <MediaDimensions width={width} height={height} length={length} imageSrc={dimensionsImage} />
              ) : (
                <VehicleSize width={width} height={height} length={length} />
              )}
            </SectionWrapper>
          )}
          {hasBasicDataSheet && (
            <SectionWrapper title={i18n.t('COMMON.BASIC_INFO')}>
              <div className={`${BASE_CLASS}-panel`}>
                <ListModelDetails listType={listTypes.icon} modelDetails={basicDataSheet} withWrapper={false} />
              </div>
              {hasJato && <p className={PANEL_EQUIPMENT_INFO_CLASS}>{jatoDescription}</p>}
            </SectionWrapper>
          )}
          {isMobileBestOffersSection && (
            <SectionWrapper>
              <div id={BEST_OFFERS_SECTION_ELEMENT_ID} ref={bestOffersSectionRef}>
                <PanelBestOffers
                  title={i18n.t('COMMON_RECOMMENDED_PLURAL')}
                  bestOffers={bestOffers}
                  linkFactory={LinkFactory}
                />
              </div>
            </SectionWrapper>
          )}
          <SectionWrapper title={mainEquipmentTitle}>
            <ListModelDetails
              listType={listTypes.accordion}
              withWrapper={false}
              isPaddingless
              groupedModelDetails={pageInfoResponse.groupedSpecifications}
            />
            {hasEquipment ? (
              <PanelEquipment equipment={equipment} displayType={panelEquipmentDisplayTypes.accordion} />
            ) : (
              <></>
            )}
            {hasJato && <p className={MODEL_DETAILS_INFO_CLASS}>{jatoDescription}</p>}
          </SectionWrapper>
          {hasNewAds && (
            <SectionWrapper>
              <ListRelatedAds
                ads={adsNew}
                display={isHigherThanMobileResolution ? listRelatedAdsDisplay.grid : listRelatedAdsDisplay.scrolling}
                itemsPerRow={MAX_NUM_OF_ADS}
                link={
                  <AtomButton design={atomButtonDesigns.LINK} href={linkToNewAds} link>
                    {i18n.t('MODEL_DETAIL.ADS.CHECK_NEW_ADS_LINK', {model: modelName})}
                  </AtomButton>
                }
                title={i18n.t('MODEL_DETAIL.ADS.TITLE_NEW', {makeModel})}
              />
            </SectionWrapper>
          )}
          {hasRentingAds && (
            <SectionWrapper>
              <ListRelatedAds
                ads={adsRenting}
                display={isHigherThanMobileResolution ? listRelatedAdsDisplay.grid : listRelatedAdsDisplay.scrolling}
                itemsPerRow={MAX_NUM_OF_ADS}
                link={
                  <AtomButton design={atomButtonDesigns.LINK} href={linkToRenting} link>
                    {i18n.t('MODEL_DETAIL.ADS.CHECK_RENTING_ADS_LINK', {
                      model: modelName
                    })}
                  </AtomButton>
                }
                title={i18n.t('MODEL_DETAIL.ADS.TITLE_RENTING', {makeModel})}
              />
            </SectionWrapper>
          )}
          {hasKm0Ads && (
            <SectionWrapper>
              <ListRelatedAds
                ads={adsKm0}
                display={isHigherThanMobileResolution ? listRelatedAdsDisplay.grid : listRelatedAdsDisplay.scrolling}
                itemsPerRow={MAX_NUM_OF_ADS}
                link={
                  <AtomButton design={atomButtonDesigns.LINK} href={linkToKm0Ads} link>
                    {i18n.t('MODEL_DETAIL.ADS.CHECK_KM0_ADS_LINK', {model: modelName})}
                  </AtomButton>
                }
                title={i18n.t('MODEL_DETAIL.ADS.TITLE_KM0', {makeModel})}
              />
            </SectionWrapper>
          )}
          <SectionWrapper title={linksTitle}>
            <TechnicalDetailsPanelLinks
              makeUrlParam={makeUrlParam}
              makeName={makeName}
              modelUrlParam={modelUrlParam}
              modelName={modelName}
              hasNewVehicles={hasNewVehicles}
            />
          </SectionWrapper>
          {hasRelatedVehicles && (
            <SectionWrapper title={competitorsTitle}>
              <ListModels models={relatedVehicles} hasMakeLabel linkFactory={LinkFactory} />
            </SectionWrapper>
          )}
        </section>
        {isDesktopBestOffersSection && (
          <aside className={`${BASE_CLASS}-sidebar`}>
            <div className={`${BASE_CLASS}-sidebarContent`}>
              <PanelBestOffers
                title={i18n.t('COMMON_RECOMMENDED_PLURAL')}
                bestOffers={bestOffers}
                linkFactory={LinkFactory}
              />
            </div>
          </aside>
        )}
        {hasBestOffers && (
          <ButtonSticky fullWidth hide={isBestOffersOnScreen} onClick={_handleScrollToBestOffers}>
            {i18n.t('COMMON_RECOMMENDED_OFFERS')}
          </ButtonSticky>
        )}
      </main>

      <SectionWrapper>
        <MotorTagCloud initialTagclouds={tagclouds} pageName={pageName} makeId={makeId} modelId={modelId} />
      </SectionWrapper>
      <Breadcrumb items={breadcrumbs} linkFactory={LinkFactory} />

      <Helmet {...helmet} />
      <TrackingPageView
        pageName={pageName}
        make={makeName?.toLowerCase()}
        model={modelName?.toLowerCase()}
        {...(versionName && {version: versionName.toLowerCase()})}
      />
      <AdDisplay pageName={pageName} make={make} model={model} bodyType={bodyType} fuelType={fuelType} />
      <MotorTrackingCriteo />
    </Layout.Basic>
  ) : (
    <></>
  )
}

TechnicalDetailsVehicle.displayName = 'TechnicalDetailsVehicle'

TechnicalDetailsVehicle.propTypes = {
  bodyType: PropTypes.string,
  breadcrumbs: PropTypes.array,
  doors: PropTypes.number,
  hasJato: PropTypes.bool,
  hasNewVehicles: PropTypes.bool,
  helmet: PropTypes.object,
  loadThirdParties: PropTypes.bool,
  makeId: PropTypes.string,
  makeName: PropTypes.string,
  makeUrlParam: PropTypes.string,
  modelId: PropTypes.string,
  modelName: PropTypes.string,
  modelUrlParam: PropTypes.string,
  pageInfoResponse: PropTypes.object,
  pageName: PropTypes.string,
  serverErrors: PropTypes.array,
  tagclouds: PropTypes.array,
  versionId: PropTypes.string,
  versionName: PropTypes.string,
  year: PropTypes.number
}

TechnicalDetailsVehicle.getInitialProps = traceInitialProps(getInitialProps)

export default withThirdPartiesParam(TechnicalDetailsVehicle)
